import { trackApiActivity } from "../../../../lib/auth";

export class BaseClient {
  protected baseUrl: string;
  protected token: string | null;

  /**
   * Request cache for deduplication
   *
   * Purpose:
   * Prevents duplicate simultaneous requests with identical parameters.
   * When multiple components call the same API simultaneously, only one
   * actual network request is made and the result is shared.
   *
   * Cache TTL: Configured via NUXT_PUBLIC_CACHE_REQUEST_DEDUPLICATION_TTL (default: 100ms)
   * This short window catches rapid duplicate calls without stale data issues.
   */
  private requestCache = new Map<string, Promise<unknown>>();

  /**
   * Cache TTL in milliseconds (from runtime config)
   */
  private cacheTTL: number;

  constructor(baseUrl: string, token: string | null) {
    this.baseUrl = baseUrl;
    this.token = token;

    // Get cache TTL from runtime config (only in client-side)
    if (import.meta.client) {
      const config = useRuntimeConfig();
      this.cacheTTL = config.public.cache.requestDeduplicationTTL;
    } else {
      this.cacheTTL = 100; // Default for SSR
    }
  }

  protected async get<T>(
    endpoint: string,
    options: {
      params?: object;
      signal?: AbortSignal | null;
    } = {},
  ): Promise<T> {
    // Track user activity for session management
    trackApiActivity();

    // Apply chaos effects if enabled
    const { applyChaos } = useChaos();
    await applyChaos(endpoint);

    // Generate cache key for deduplication
    const cacheKey = this.generateCacheKey("GET", endpoint, options.params);

    // Execute with deduplication
    return this.executeWithCache(cacheKey, () =>
      $fetch<T>(apiUrlCombine(this.baseUrl, endpoint), {
        method: "GET",
        query: this.buildQuery(options.params),
        signal: options.signal || undefined,
        headers: this.getHeaders(),
      }),
    );
  }

  protected async post<T>(
    endpoint: string,
    options: {
      body?: unknown;
      params?: object;
      signal?: AbortSignal | null;
    } = {},
  ): Promise<T> {
    // Track user activity for session management
    trackApiActivity();

    // Apply chaos effects if enabled
    const { applyChaos } = useChaos();
    await applyChaos(endpoint);

    // Generate cache key for deduplication (includes body)
    const cacheKey = this.generateCacheKey("POST", endpoint, {
      params: options.params,
      body: options.body,
    });

    // Execute with deduplication
    return this.executeWithCache(cacheKey, () =>
      $fetch<T>(apiUrlCombine(this.baseUrl, endpoint), {
        method: "POST",
        body: options.body as Record<string, any> | BodyInit | null | undefined,
        query: this.buildQuery(options.params),
        signal: options.signal || undefined,
        headers: this.getHeaders(),
      }),
    );
  }

  protected async put<T>(
    endpoint: string,
    options: {
      body?: unknown;
      params?: object;
      signal?: AbortSignal | null;
    } = {},
  ): Promise<T> {
    // Track user activity for session management
    trackApiActivity();

    // Apply chaos effects if enabled
    const { applyChaos } = useChaos();
    await applyChaos(endpoint);

    // Generate cache key for deduplication (includes body)
    const cacheKey = this.generateCacheKey("PUT", endpoint, {
      params: options.params,
      body: options.body,
    });

    // Execute with deduplication
    return this.executeWithCache(cacheKey, () =>
      $fetch<T>(apiUrlCombine(this.baseUrl, endpoint), {
        method: "PUT",
        body: options.body as Record<string, any> | BodyInit | null | undefined,
        query: this.buildQuery(options.params),
        signal: options.signal || undefined,
        headers: this.getHeaders(),
      }),
    );
  }

  protected async patch<T>(
    endpoint: string,
    options: {
      body?: unknown;
      params?: object;
      signal?: AbortSignal | null;
    } = {},
  ): Promise<T> {
    // Track user activity for session management
    trackApiActivity();

    // Apply chaos effects if enabled
    const { applyChaos } = useChaos();
    await applyChaos(endpoint);

    // Generate cache key for deduplication (includes body)
    const cacheKey = this.generateCacheKey("PATCH", endpoint, {
      params: options.params,
      body: options.body,
    });

    // Execute with deduplication
    return this.executeWithCache(cacheKey, () =>
      $fetch<T>(apiUrlCombine(this.baseUrl, endpoint), {
        method: "PATCH",
        body: options.body as Record<string, any> | BodyInit | null | undefined,
        query: this.buildQuery(options.params),
        signal: options.signal || undefined,
        headers: this.getHeaders(),
      }),
    );
  }

  protected async delete<T>(
    endpoint: string,
    options: {
      params?: object;
      signal?: AbortSignal | null;
    } = {},
  ): Promise<T> {
    // Track user activity for session management
    trackApiActivity();

    // Apply chaos effects if enabled
    const { applyChaos } = useChaos();
    await applyChaos(endpoint);

    // Generate cache key for deduplication
    const cacheKey = this.generateCacheKey("DELETE", endpoint, options.params);

    // Execute with deduplication
    return this.executeWithCache(cacheKey, () =>
      $fetch<T>(apiUrlCombine(this.baseUrl, endpoint), {
        method: "DELETE",
        query: this.buildQuery(options.params),
        signal: options.signal || undefined,
        headers: this.getHeaders(),
      }),
    );
  }

  private buildQuery(params?: object): Record<string, string> {
    if (!params) return {};
    return Object.entries(params).reduce(
      (acc, [key, val]) => {
        if (val !== undefined && val !== null && val !== "") acc[key] = String(val);
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    return headers;
  }

  /**
   * Generate unique cache key for request deduplication
   *
   * Purpose:
   * Creates a consistent key for caching based on HTTP method, endpoint, and parameters.
   * Identical requests (same method + endpoint + params) get the same key.
   *
   * @param method - HTTP method (GET, POST, PUT, PATCH, DELETE)
   * @param endpoint - API endpoint path
   * @param params - Query parameters or request body
   * @returns Unique cache key string
   */
  private generateCacheKey(
    method: string,
    endpoint: string,
    params?: Record<string, any> | any,
  ): string {
    const paramsKey = params ? JSON.stringify(params) : "";
    return `${method}:${endpoint}:${paramsKey}`;
  }

  /**
   * Execute request with deduplication
   *
   * Purpose:
   * Wraps $fetch calls to prevent duplicate simultaneous requests.
   * If identical request is in flight, returns the existing promise.
   *
   * AbortSignal Handling:
   * When request is aborted via signal, the promise rejects but stays in cache
   * for 100ms. Subsequent identical requests within that window will also abort.
   * This is intentional - if user navigated away, all duplicate requests should abort.
   *
   * @param cacheKey - Unique key for this request
   * @param requestFn - Function that returns the $fetch promise
   * @returns Response from API
   */
  private async executeWithCache<T>(
    cacheKey: string,
    requestFn: () => Promise<unknown>,
  ): Promise<T> {
    // Check if identical request is already in flight
    if (this.requestCache.has(cacheKey)) {
      // Return existing promise (deduplicated!)
      return this.requestCache.get(cacheKey) as Promise<T>;
    }

    // No cached request - make new one
    const promise = requestFn();

    // Cache the promise (not the result)
    this.requestCache.set(cacheKey, promise);

    // Auto-cleanup after request completes (success or error)
    promise.finally(() => {
      // Short delay before clearing cache (uses configured TTL)
      setTimeout(() => {
        this.requestCache.delete(cacheKey);
      }, this.cacheTTL);
    });

    return promise as Promise<T>;
  }
}
